import { reportUiFailure } from "../report-failure.js";

export interface DesktopClaimResult {
  status?: string;
  error?: string;
  // The claim gate's stable code beside `error`; absent from an older desktop shell.
  errorCode?: string;
}

export function parseDesktopClaimResult(res: unknown): DesktopClaimResult {
  if (typeof res !== "object" || res === null) return {};
  const { status, error, errorCode } = res as Record<string, unknown>; // cast: fields are narrowed below
  return {
    ...(typeof status === "string" ? { status } : {}),
    ...(typeof error === "string" && error ? { error } : {}),
    ...(typeof errorCode === "string" && errorCode ? { errorCode } : {}),
  };
}

// The shell's `error` prose can name a relay host: only the status enum leaves the machine.
export function reportDesktopClaimError(result: DesktopClaimResult): void {
  if (!result.error) return;
  reportUiFailure(
    "desktop.claim_daemon",
    new Error("desktop daemon claim returned an error"),
    { status: result.status },
    { code: result.errorCode ?? "claim_error" },
  );
}

// ─── The desktop shell's daemon claim ──────────────────────────────────────

interface DesktopBridge {
  invoke: (command: string, args?: Record<string, unknown>) => Promise<unknown>;
}

function desktopBridge(): DesktopBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { cyborg7Desktop?: DesktopBridge }).cyborg7Desktop; // cast: preload global
}

export interface DesktopClaimArgs {
  ownerId: string;
  relayUrl: string;
  userToken: string;
}

// The signed-in session's claim, kept so onboarding can repeat it on a person's click
// ("Use this computer with this account") without re-deriving the relay or the token.
let lastClaim: DesktopClaimArgs | undefined;

export function rememberDesktopClaim(args: DesktopClaimArgs): void {
  lastClaim = args;
}

// Bind this desktop's daemon to the signed-in account; `replaceOtherAccount` only on an
// explicit local click. Not async: a broken bridge throws synchronously into the
// login's try, which then skips the rest of the desktop wiring, as it always did.
export function claimThisComputer(
  opts: { replaceOtherAccount?: boolean } = {},
): Promise<DesktopClaimResult> {
  const desktop = desktopBridge();
  if (!desktop || !lastClaim) {
    return Promise.resolve({ error: "not signed in inside the desktop app" });
  }
  return desktop
    .invoke("claim_desktop_daemon", {
      ...lastClaim,
      ...(opts.replaceOtherAccount ? { replaceOtherAccount: true } : {}),
    })
    .then(parseDesktopClaimResult);
}

export interface DesktopLinkStatus {
  // Who this machine's daemon runs as; compared against the signed-in user.
  ownerId: string | null; // null: never claimed
  // This session's last owner check. null: none ran (the machine was already ours).
  lastClaim: { outcome: string; at: number } | null;
}

// Undefined outside the desktop app, or on a shell too old to answer.
export async function readDesktopLinkStatus(): Promise<DesktopLinkStatus | undefined> {
  const desktop = desktopBridge();
  if (!desktop) return undefined;
  try {
    const res = (await desktop.invoke("desktop_daemon_link_status")) as Partial<DesktopLinkStatus>;
    return {
      ownerId: typeof res?.ownerId === "string" ? res.ownerId : null, // null: never claimed
      lastClaim: res?.lastClaim && typeof res.lastClaim.outcome === "string" ? res.lastClaim : null,
    };
  } catch {
    return undefined; // intentional: an older shell has no such command — nothing to show
  }
}
